import React, { FC, useCallback, useContext, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { NotificationContext } from '../../contexts/NotificationContext';
import { EditUserForm } from './EditUserForm';
import { useTranslation } from 'react-i18next';
import { joiResolver } from '@hookform/resolvers/joi';
import { userFormSchema } from '../../formSchemas/userFormSchema';
import { UserFetcher } from '../../fetchers/UserFetcher';
import { apiUser, TUpdateUserPayload } from '../../resources/api/apiUser';
import { useApiErrorHandler } from '../../hooks/useApiErrorHandler';
import { useResourceChecker } from '../../resources/resource';
import Box from '@mui/system/Box';
import Alert from '@mui/material/Alert';

interface IProps {
  open: boolean;
  closeDialog: () => void;
}

const { useUpdateUser, useRequestEmailChange } = apiUser;

export const EditUserDialog: FC<IProps> = ({ open, closeDialog }) => {
  const { data, onDataUpdate } = useContext(UserFetcher.Context);
  const checkResource = useResourceChecker();

  const { execute: requestEmailChange, resource: resourceEmailChange } = useRequestEmailChange(data?.payload.id);
  const { execute: updateUser } = useUpdateUser(data?.payload.id);

  const canChangeEmail = checkResource(resourceEmailChange);

  const handleError = useApiErrorHandler();

  const { addNotification } = useContext(NotificationContext);
  const { t } = useTranslation();

  const { control, handleSubmit, formState, watch, reset } = useForm({
    resolver: joiResolver(userFormSchema),
  });

  const email = watch('email');
  const emailChanged = canChangeEmail && email !== data?.payload.email;

  const onSubmit = useCallback(
    async (fields: TUpdateUserPayload & { email: string }) => {
      if (!data) return;
      const payload: Partial<TUpdateUserPayload> = {};
      const { email, ...updateUserFields } = fields;

      if (updateUserFields.phone_number === '') {
        updateUserFields.phone_number = null;
      }

      let fieldsToUpdate = false;
      const emailToUpdate = canChangeEmail && email !== data.payload.email;

      (
        Object.entries(updateUserFields) as [keyof TUpdateUserPayload, TUpdateUserPayload[keyof TUpdateUserPayload]][]
      ).forEach(<T extends keyof TUpdateUserPayload>([field, value]: [T, TUpdateUserPayload[T]]) => {
        if (value !== data.payload[field]) {
          fieldsToUpdate = true;
          payload[field] = value;
        }
      });
      if (fieldsToUpdate || emailToUpdate) {
        try {
          if (fieldsToUpdate) {
            const newUserDetails = await updateUser(payload);

            onDataUpdate({
              ...data,
              payload: {
                ...data.payload,
                ...newUserDetails.payload,
              },
            });

            addNotification({
              severity: 'success',
              message: t('editUserDialog.edit.success'),
            });
          }
          if (emailToUpdate) {
            await requestEmailChange(email);
            addNotification({
              severity: 'success',
              message: t('editUserDialog.edit.emailChangeRequested'),
            });
          }
          closeDialog();
        } catch (e) {
          handleError(e);
        }
      } else {
        addNotification({
          severity: 'warning',
          message: t('editUserDialog.edit.nothingChanged'),
        });
      }
    },
    [data, updateUser, closeDialog, canChangeEmail]
  );

  useEffect(() => {
    if (open) {
      reset({
        first_name: data?.payload.first_name ?? '',
        last_name: data?.payload.last_name ?? '',
        phone_number: data?.payload.phone_number ?? '',
        email: data?.payload.email ?? '',
      });
    }
  }, [open]);

  if (!data) return null;
  return (
    <Dialog open={open} onClose={closeDialog}>
      <DialogTitle>{t('editUserDialog.edit.title')}</DialogTitle>
      <DialogContent>
        <EditUserForm
          onSubmit={onSubmit}
          user={data.payload}
          control={control}
          handleSubmit={handleSubmit}
          formState={formState}
          showEmail={canChangeEmail}
        />
        {emailChanged ? (
          <Box mt={2}>
            <Alert severity={'info'}>{t('editUserDialog.edit.emailChange')}</Alert>
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog}>{t('common.dialog.close')}</Button>
        <Button variant="contained" color="primary" onClick={handleSubmit(onSubmit)}>
          {t('editUserDialog.edit.confirmButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
